import axios from "axios";
import { API_BASE_URL } from "../config/config";
import { handleApiResponse, handleApiError } from "../utils/apiResponse";

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // For login endpoint, handle differently - just return the response as-is
    if (response.config.url?.includes("/auth/login")) {
      return response;
    }

    // For blob responses (like file downloads), return directly
    if (response.config.responseType === "blob") {
      return response;
    }

    // Backend returns responses with {data: ..., message: ...} format
    // If response has data field, consider it successful
    if (response.data && response.data.data !== undefined) {
      return response;
    }

    // Handle error responses that might have success field
    const result = handleApiResponse(response);
    if (result.success) {
      return response;
    } else {
      // Convert failed success responses to errors
      const error = new Error(result.message || "Request failed");
      error.response = response;
      error.apiError = result.error;
      return Promise.reject(error);
    }
  },
  (error) => {
    // Handle different error scenarios
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");

      // ONLY redirect if we are NOT on the login page already
      // and this wasn't a login request failing (which would be a 401 for wrong credentials)
      const isLoginRequest = error.config.url?.includes("/auth/login");
      const isLoginPage = window.location.pathname === "/login";

      if (!isLoginRequest && !isLoginPage) {
        window.location.href = "/login";
      }
    } else if (error.response?.status === 403) {
      // Forbidden - insufficient permissions
      console.error(
        "Access denied:",
        error.response?.data?.error?.message || error.response?.data?.message,
      );
    } else if (error.response?.status >= 500) {
      // Server error
      console.error(
        "Server error:",
        error.response?.data?.error?.message || error.response?.data?.message,
      );
    }

    // Attach standardized error information
    const errorResult = handleApiError(error);
    error.apiError = errorResult.error;
    error.apiMessage = errorResult.message;

    return Promise.reject(error);
  },
);

// API endpoints
export const endpoints = {
  // Health check
  health: "/health",
  dbStats: "/api/v1/dashboard/health",

  // Test endpoints
  test: "/api/v1/test",
  ping: "/api/v1/ping",

  // Authentication
  auth: {
    login: "/api/v1/auth/login",
    logout: "/api/v1/auth/logout",
    refresh: "/api/v1/auth/refresh",
    me: "/api/v1/auth/me",
    logoutAll: "/api/v1/auth/logout-all",
    sessions: "/api/v1/auth/sessions",
    loginHistory: "/api/v1/auth/login-history",
  },

  // Online status
  online: {
    users: "/api/v1/online/users",
    stats: "/api/v1/online/stats",
    forceLogout: (userId) => `/api/v1/online/logout/${userId}`,
  },

  // User management
  users: {
    list: "/api/v1/users",
    create: "/api/v1/users",
    get: (id) => `/api/v1/users/${id}`,
    update: (id) => `/api/v1/users/${id}`,
    delete: (id) => `/api/v1/users/${id}`,
    updateStatus: (id) => `/api/v1/users/${id}/status`,
  },

  // Token management
  tokens: {
    list: "/api/v1/tokens",
    create: "/api/v1/tokens",
    get: (id) => `/api/v1/tokens/${id}`,
    update: (id) => `/api/v1/tokens/${id}`,
    delete: (id) => `/api/v1/tokens/${id}`,
    updateStatus: (id) => `/api/v1/tokens/${id}/status`,
    stats: "/api/v1/tokens/stats",
  },

  // Trade volume
  trades: {
    list: "/api/v1/trades",
    create: "/api/v1/api-trades", // Changed for upload
    createBatch: "/api/v1/api-trades/batch", // Changed for upload
    createManual: "/api/v1/trades",
    get: (id) => `/api/v1/trades/${id}`,
    update: (id) => `/api/v1/trades/${id}`,
    delete: (id) => `/api/v1/trades/${id}`,
    deleteBatch: "/api/v1/trades/batch-delete",
    stats: "/api/v1/trades/stats",
    statsExchanges: "/api/v1/trades/stats/exchanges",
    statsClassified: "/api/v1/trades/stats/classified",
    statsPairs: "/api/v1/trades/stats/pairs",
    statsDaily: "/api/v1/trades/stats/daily",
    activeAccounts: "/api/v1/trades/stats/accounts",
    trendAccounts: "/api/v1/trades/trend/accounts",
    trendAccountPairs: "/api/v1/trades/trend/account-pairs",
    trendExchangeDaily: "/api/v1/trades/trend/exchange-daily",
    trendPairs: "/api/v1/trades/trend/pairs",
  },

  // Reports
  reports: {
    list: "/api/v1/reports",
    generate: "/api/v1/reports",
    get: (id) => `/api/v1/reports/${id}`,
    download: (id) => `/api/v1/reports/${id}/download`,
    data: (id) => `/api/v1/reports/${id}/data`,
    delete: (id) => `/api/v1/reports/${id}`,
  },

  // Dashboard
  dashboard: {
    stats: "/api/v1/dashboard/stats",
    charts: "/api/v1/dashboard/charts",
    recent: "/api/v1/dashboard/recent",
  },

  // Exchanges
  exchanges: {
    list: "/api/v1/exchanges",
    active: "/api/v1/exchanges/active",
    get: (id) => `/api/v1/exchanges/${id}`,
    create: "/api/v1/exchanges",
    update: (id) => `/api/v1/exchanges/${id}`,
    delete: (id) => `/api/v1/exchanges/${id}`,
    updateStatus: (id) => `/api/v1/exchanges/${id}/status`,
  },

  // Account balances
  balances: {
    list: "/api/v1/balances",
    stats: "/api/v1/balances/stats",
    summaries: "/api/v1/balances/summaries",
    delete: (id) => `/api/v1/balances/${id}`,
    update: (id) => `/api/v1/balances/${id}`,
    deleteBatch: "/api/v1/balances/batch-delete",
    // API Token 上报接口
    report: "/api/v1/api-balances",
    reportBatch: "/api/v1/api-balances/batch",
    createManual: "/api/v1/balances",
  },

  // Account configs
  accountConfigs: {
    list: "/api/v1/account-configs",
    get: (id) => `/api/v1/account-configs/${id}`,
    create: "/api/v1/account-configs",
    update: (id) => `/api/v1/account-configs/${id}`,
    delete: (id) => `/api/v1/account-configs/${id}`,
    batchUpdate: "/api/v1/account-configs/batch-update",
    // Borrow configs
    addBorrow: (id) => `/api/v1/account-configs/${id}/borrows`,
    updateBorrow: (id, borrowId) => `/api/v1/account-configs/${id}/borrows/${borrowId}`,
    deleteBorrow: (id, borrowId) => `/api/v1/account-configs/${id}/borrows/${borrowId}`,
  },

  // 业务线主数据
  businessLines: {
    list: "/api/v1/business-lines",
    active: "/api/v1/business-lines/active",
    get: (id) => `/api/v1/business-lines/${id}`,
    create: "/api/v1/business-lines",
    update: (id) => `/api/v1/business-lines/${id}`,
    delete: (id) => `/api/v1/business-lines/${id}`,
  },

  // 策略类型主数据
  strategyTypes: {
    list: "/api/v1/strategy-types",
    active: "/api/v1/strategy-types/active",
    get: (id) => `/api/v1/strategy-types/${id}`,
    create: "/api/v1/strategy-types",
    update: (id) => `/api/v1/strategy-types/${id}`,
    delete: (id) => `/api/v1/strategy-types/${id}`,
  },

  // Transactions
  transactions: {
    list: "/api/v1/transactions",
    get: (id) => `/api/v1/transactions/${id}`,
    create: "/api/v1/transactions",
    update: (id) => `/api/v1/transactions/${id}`,
    delete: (id) => `/api/v1/transactions/${id}`,
    stats: "/api/v1/transactions/stats",
    balanceSummary: "/api/v1/transactions/balance-summary",
    internalTransfer: "/api/v1/transactions/internal-transfer",
  },

  // Admin
  admin: {
    users: "/api/v1/admin/users",
    updateUserRole: (id) => `/api/v1/admin/users/${id}/role`,
    tokens: "/api/v1/admin/tokens",
    logs: "/api/v1/admin/logs",
    system: "/api/v1/admin/system",
  },

  // Equity
  equity: {
    trends: "/api/v1/equity/trends",
    trendsTotal: "/api/v1/equity/trends/total",
    trendsAccounts: "/api/v1/equity/trends/accounts",
    trendsExchanges: "/api/v1/equity/trends/exchanges",
    trendsBusinessLines: "/api/v1/equity/trends/business-lines",
    trendsStrategyTypes: "/api/v1/equity/trends/strategy-types",
    trendsAssets: "/api/v1/equity/trends/assets",
    assets: "/api/v1/equity/assets",
    recalculate: "/api/v1/equity/recalculate",
    recalculateAll: "/api/v1/equity/recalculate-all",
  },

  // Hedge groups
  hedgeGroups: {
    list: "/api/v1/hedge-groups",
    create: "/api/v1/hedge-groups",
    update: (id) => `/api/v1/hedge-groups/${id}`,
    delete: (id) => `/api/v1/hedge-groups/${id}`,
    trends: (id) => `/api/v1/hedge-groups/${id}/trends`,
  },

  // Alert rules
  alertRules: {
    list: "/api/v1/alert-rules",
    create: "/api/v1/alert-rules",
    get: (id) => `/api/v1/alert-rules/${id}`,
    update: (id) => `/api/v1/alert-rules/${id}`,
    delete: (id) => `/api/v1/alert-rules/${id}`,
    toggle: (id) => `/api/v1/alert-rules/${id}/toggle`,
  },

  // Alerts
  alerts: {
    list: "/api/v1/alerts",
    get: (id) => `/api/v1/alerts/${id}`,
    acknowledge: (id) => `/api/v1/alerts/${id}/acknowledge`,
    resolve: (id) => `/api/v1/alerts/${id}/resolve`,
    markAsRead: (id) => `/api/v1/alerts/${id}/read`,
    markAllAsRead: "/api/v1/alerts/read-all",
    deleteBatch: "/api/v1/alerts/batch-delete",
    resolveBatch: "/api/v1/alerts/batch-resolve",
    stats: "/api/v1/alerts/stats",
  },

  // Scheduled tasks
  tasks: {
    list: "/api/v1/tasks",
    create: "/api/v1/tasks",
    get: (id) => `/api/v1/tasks/${id}`,
    update: (id) => `/api/v1/tasks/${id}`,
    delete: (id) => `/api/v1/tasks/${id}`,
    toggle: (id) => `/api/v1/tasks/${id}/toggle`,
    run: (id) => `/api/v1/tasks/${id}/run`,
    logs: (id) => `/api/v1/tasks/${id}/logs`,
  },

  // Notification channels
  notificationChannels: {
    list: "/api/v1/notification-channels",
    create: "/api/v1/notification-channels",
    get: (id) => `/api/v1/notification-channels/${id}`,
    update: (id) => `/api/v1/notification-channels/${id}`,
    delete: (id) => `/api/v1/notification-channels/${id}`,
    toggle: (id) => `/api/v1/notification-channels/${id}/toggle`,
    test: (id) => `/api/v1/notification-channels/${id}/test`,
  },

  // Analytics
  analytics: {
    compare: "/api/v1/analytics/compare",
    attribution: "/api/v1/analytics/attribution",
    forecast: "/api/v1/analytics/forecast",
  },

  // Monitor
  monitor: {
    metrics: "/api/v1/monitor/metrics",
    health: "/api/v1/monitor/health",
  },

  // Audit
  audit: {
    logs: "/api/v1/audit/logs",
  },
};

// API service functions
export const apiService = {
  // Health check
  checkHealth: () => api.get(endpoints.health),
  getDbStats: () => api.get(endpoints.dbStats),

  // Test endpoints
  test: () => api.get(endpoints.test),
  ping: () => api.get(endpoints.ping),

  // Authentication
  auth: {
    login: (credentials) => api.post(endpoints.auth.login, credentials),
    logout: () => api.post(endpoints.auth.logout),
    refresh: (refreshToken) =>
      api.post(endpoints.auth.refresh, { refresh_token: refreshToken }),
    getMe: () => api.get(endpoints.auth.me),
    logoutAll: () => api.post(endpoints.auth.logoutAll),
    getSessions: () => api.get(endpoints.auth.sessions),
    getLoginHistory: (params) =>
      api.get(endpoints.auth.loginHistory, { params }),
    changePassword: (data) => api.put("/api/v1/auth/password", data),
  },

  // User management
  users: {
    getUsers: (params) => api.get(endpoints.users.list, { params }),
    createUser: (userData) => api.post(endpoints.users.create, userData),
    getUser: (id) => api.get(endpoints.users.get(id)),
    updateUser: (id, userData) => api.put(endpoints.users.update(id), userData),
    deleteUser: (id) => api.delete(endpoints.users.delete(id)),
    updateUserStatus: (id, status) =>
      api.put(endpoints.users.updateStatus(id), { status }),
  },

  // Token management
  tokens: {
    getTokens: (params) => api.get(endpoints.tokens.list, { params }),
    createToken: (tokenData) => api.post(endpoints.tokens.create, tokenData),
    getToken: (id) => api.get(endpoints.tokens.get(id)),
    updateToken: (id, tokenData) =>
      api.put(endpoints.tokens.update(id), tokenData),
    deleteToken: (id) => api.delete(endpoints.tokens.delete(id)),
    updateTokenStatus: (id, status) =>
      api.put(endpoints.tokens.updateStatus(id), { status }),
    getTokenStats: () => api.get(endpoints.tokens.stats),
  },

  // Trade volume
  trades: {
    getTrades: (params) => api.get(endpoints.trades.list, { params }),
    createTradeManual: (data) => api.post(endpoints.trades.createManual, data),
    createTrade: (tradeData) => api.post(endpoints.trades.create, tradeData),
    createTradesBatch: (tradesData) =>
      api.post(endpoints.trades.createBatch, tradesData),
    getTrade: (id) => api.get(endpoints.trades.get(id)),
    updateTrade: (id, tradeData) =>
      api.put(endpoints.trades.update(id), tradeData),
    deleteTrade: (id) => api.delete(endpoints.trades.delete(id)),
    deleteTradesBatch: (ids) =>
      api.post(endpoints.trades.deleteBatch, { ids }),
    getTradeStats: (params) => api.get(endpoints.trades.stats, { params }),
    getExchangeStats: (params) =>
      api.get(endpoints.trades.statsExchanges, { params }),
    getClassifiedTradeStats: (params) =>
      api.get(endpoints.trades.statsClassified, { params }),
    getPairStats: (params) => api.get(endpoints.trades.statsPairs, { params }),
    getDailyStats: (params) => api.get(endpoints.trades.statsDaily, { params }),
    getActiveAccounts: (params) => api.get(endpoints.trades.activeAccounts, { params }),
    getAccountTrendData: (params) => api.get(endpoints.trades.trendAccounts, { params }),
    getAccountPairTrendData: (params) =>
      api.get(endpoints.trades.trendAccountPairs, { params }),
    getExchangeDailyTrendData: (params) =>
      api.get(endpoints.trades.trendExchangeDaily, { params }),
    getPairTrendData: (params) => api.get(endpoints.trades.trendPairs, { params }),
  },

  // Reports
  reports: {
    getReports: (params) => api.get(endpoints.reports.list, { params }),
    generateReport: (reportData) =>
      api.post(endpoints.reports.generate, reportData),
    getReport: (id) => api.get(endpoints.reports.get(id)),
    downloadReport: (id, format = 'excel') =>
      api.get(`${endpoints.reports.download(id)}?format=${format}`, { responseType: "blob" }),
    getReportData: (id) => api.get(endpoints.reports.data(id)),
    deleteReport: (id) => api.delete(endpoints.reports.delete(id)),
  },

  // Dashboard
  dashboard: {
    getStats: () => api.get(endpoints.dashboard.stats),
    getCharts: (params) => api.get(endpoints.dashboard.charts, { params }),
    getRecentActivity: (params) =>
      api.get(endpoints.dashboard.recent, { params }),
  },

  // Exchanges
  exchanges: {
    getActiveExchanges: () => api.get(endpoints.exchanges.active),
    getExchanges: (params) => api.get(endpoints.exchanges.list, { params }),
    getExchange: (id) => api.get(endpoints.exchanges.get(id)),
    createExchange: (data) => api.post(endpoints.exchanges.create, data),
    updateExchange: (id, data) => api.put(endpoints.exchanges.update(id), data),
    deleteExchange: (id) => api.delete(endpoints.exchanges.delete(id)),
    updateExchangeStatus: (id, is_active) =>
      api.put(endpoints.exchanges.updateStatus(id), { is_active }),
  },

  // Account balances
  balances: {
    getBalances: (params) => api.get(endpoints.balances.list, { params }),
    createBalanceManual: (data) => api.post(endpoints.balances.createManual, data),
    getStats: (params) => api.get(endpoints.balances.stats, { params }),
    getSummaries: (params) => api.get(endpoints.balances.summaries, { params }),
    deleteBalance: (id) => api.delete(endpoints.balances.delete(id)),
    updateBalance: (id, data) => api.put(endpoints.balances.update(id), data),
    deleteBalancesBatch: (ids) =>
      api.post(endpoints.balances.deleteBatch, { ids }),
    // 上报接口（需要 API Token，一般通过第三方调用）
    report: (data) => api.post(endpoints.balances.report, data),
    reportBatch: (data) => api.post(endpoints.balances.reportBatch, data),
  },

  // Account configs
  accountConfigs: {
    getConfigs: (params) => api.get(endpoints.accountConfigs.list, { params }),
    getConfig: (id) => api.get(endpoints.accountConfigs.get(id)),
    createConfig: (data) => api.post(endpoints.accountConfigs.create, data),
    updateConfig: (id, data) => api.put(endpoints.accountConfigs.update(id), data),
    deleteConfig: (id) => api.delete(endpoints.accountConfigs.delete(id)),
    batchUpdate: (data) => api.post(endpoints.accountConfigs.batchUpdate, data),
    addBorrowConfig: (id, data) => api.post(endpoints.accountConfigs.addBorrow(id), data),
    updateBorrowConfig: (id, borrowId, data) => api.put(endpoints.accountConfigs.updateBorrow(id, borrowId), data),
    deleteBorrowConfig: (id, borrowId) => api.delete(endpoints.accountConfigs.deleteBorrow(id, borrowId)),
  },

  businessLines: {
    list: (params) => api.get(endpoints.businessLines.list, { params }),
    listActive: () => api.get(endpoints.businessLines.active),
    get: (id) => api.get(endpoints.businessLines.get(id)),
    create: (data) => api.post(endpoints.businessLines.create, data),
    update: (id, data) => api.put(endpoints.businessLines.update(id), data),
    delete: (id) => api.delete(endpoints.businessLines.delete(id)),
  },

  strategyTypes: {
    list: (params) => api.get(endpoints.strategyTypes.list, { params }),
    listActive: () => api.get(endpoints.strategyTypes.active),
    get: (id) => api.get(endpoints.strategyTypes.get(id)),
    create: (data) => api.post(endpoints.strategyTypes.create, data),
    update: (id, data) => api.put(endpoints.strategyTypes.update(id), data),
    delete: (id) => api.delete(endpoints.strategyTypes.delete(id)),
  },

  // Transactions
  transactions: {
    getTransactions: (params) => api.get(endpoints.transactions.list, { params }),
    getTransaction: (id) => api.get(endpoints.transactions.get(id)),
    createTransaction: (data) => api.post(endpoints.transactions.create, data),
    updateTransaction: (id, data) => api.put(endpoints.transactions.update(id), data),
    deleteTransaction: (id) => api.delete(endpoints.transactions.delete(id)),
    getStats: (params) => api.get(endpoints.transactions.stats, { params }),
    getBalanceSummary: (params) => api.get(endpoints.transactions.balanceSummary, { params }),
    createInternalTransfer: (data) => api.post(endpoints.transactions.internalTransfer, data),
  },

  // Admin
  admin: {
    getUsers: (params) => api.get(endpoints.admin.users, { params }),
    updateUserRole: (id, role) =>
      api.put(endpoints.admin.updateUserRole(id), { role }),
    getTokens: (params) => api.get(endpoints.admin.tokens, { params }),
    getLogs: (params) => api.get(endpoints.admin.logs, { params }),
    getSystemInfo: () => api.get(endpoints.admin.system),
  },

  // Online status
  online: {
    getOnlineUsers: (params) => api.get(endpoints.online.users, { params }),
    getOnlineStats: () => api.get(endpoints.online.stats),
    forceLogoutUser: (userId) => api.post(endpoints.online.forceLogout(userId)),
  },

  // Equity
  equity: {
    getTrends: (params) => api.get(endpoints.equity.trends, { params }),
    getTotalTrends: (params) => api.get(endpoints.equity.trendsTotal, { params }),
    getAccountTrends: (params) => api.get(endpoints.equity.trendsAccounts, { params }),
    getExchangeTrends: (params) =>
      api.get(endpoints.equity.trendsExchanges, { params }),
    getBusinessLineTrends: (params) =>
      api.get(endpoints.equity.trendsBusinessLines, { params }),
    getStrategyTypeTrends: (params) =>
      api.get(endpoints.equity.trendsStrategyTypes, { params }),
    getAssetTrendsGrouped: (params) => api.get(endpoints.equity.trendsAssets, { params }),
    getAssetTrends: (params) => api.get(endpoints.equity.assets, { params }),
    recalculate: (data) => api.post(endpoints.equity.recalculate, data),
    recalculateAll: () => api.post(endpoints.equity.recalculateAll),
  },

  // Hedge groups
  hedgeGroups: {
    getGroups: (params) => api.get(endpoints.hedgeGroups.list, { params }),
    createGroup: (data) => api.post(endpoints.hedgeGroups.create, data),
    updateGroup: (id, data) => api.put(endpoints.hedgeGroups.update(id), data),
    deleteGroup: (id) => api.delete(endpoints.hedgeGroups.delete(id)),
    getGroupTrends: (id, params) => api.get(endpoints.hedgeGroups.trends(id), { params }),
  },

  // Alert rules
  alertRules: {
    getRules: (params) => api.get(endpoints.alertRules.list, { params }),
    createRule: (data) => api.post(endpoints.alertRules.create, data),
    getRule: (id) => api.get(endpoints.alertRules.get(id)),
    updateRule: (id, data) => api.put(endpoints.alertRules.update(id), data),
    deleteRule: (id) => api.delete(endpoints.alertRules.delete(id)),
    toggleRule: (id) => api.put(endpoints.alertRules.toggle(id)),
  },

  // Alerts
  alerts: {
    getAlerts: (params) => api.get(endpoints.alerts.list, { params }),
    getAlert: (id) => api.get(endpoints.alerts.get(id)),
    acknowledgeAlert: (id, data) => api.post(endpoints.alerts.acknowledge(id), data),
    resolveAlert: (id) => api.post(endpoints.alerts.resolve(id)),
    markAsRead: (id) => api.put(endpoints.alerts.markAsRead(id)),
    markAllAsRead: () => api.post(endpoints.alerts.markAllAsRead),
    deleteAlertsBatch: (ids) => api.post(endpoints.alerts.deleteBatch, { ids }),
    resolveAlertsBatch: (ids) => api.post(endpoints.alerts.resolveBatch, { ids }),
    getAlertStats: () => api.get(endpoints.alerts.stats),
  },

  // Scheduled tasks
  tasks: {
    getTasks: (params) => api.get(endpoints.tasks.list, { params }),
    createTask: (data) => api.post(endpoints.tasks.create, data),
    getTask: (id) => api.get(endpoints.tasks.get(id)),
    updateTask: (id, data) => api.put(endpoints.tasks.update(id), data),
    deleteTask: (id) => api.delete(endpoints.tasks.delete(id)),
    toggleTask: (id) => api.put(endpoints.tasks.toggle(id)),
    runTask: (id) => api.post(endpoints.tasks.run(id)),
    getTaskLogs: (id, params) => api.get(endpoints.tasks.logs(id), { params }),
  },

  // Notification channels
  notificationChannels: {
    getChannels: (params) => api.get(endpoints.notificationChannels.list, { params }),
    createChannel: (data) => api.post(endpoints.notificationChannels.create, data),
    getChannel: (id) => api.get(endpoints.notificationChannels.get(id)),
    updateChannel: (id, data) => api.put(endpoints.notificationChannels.update(id), data),
    deleteChannel: (id) => api.delete(endpoints.notificationChannels.delete(id)),
    toggleChannel: (id) => api.put(endpoints.notificationChannels.toggle(id)),
    testChannel: (id) => api.post(endpoints.notificationChannels.test(id)),
  },

  // Analytics
  analytics: {
    compare: (params) => api.post(endpoints.analytics.compare, params),
    attribution: (params) => api.post(endpoints.analytics.attribution, params),
    forecast: (params) => api.post(endpoints.analytics.forecast, params),
  },

  // Monitor
  monitor: {
    getMetrics: () => api.get(endpoints.monitor.metrics),
    getHealth: () => api.get(endpoints.monitor.health),
  },

  // Audit
  audit: {
    getLogs: (params) => api.get(endpoints.audit.logs, { params }),
  },
};

// Demo data generators (for development)
export const demoData = {
  // Generate demo users
  generateUsers: (count = 10) => {
    const users = [];
    for (let i = 1; i <= count; i++) {
      users.push({
        id: i,
        username: `user${i}`,
        email: `user${i}@example.com`,
        role: i <= 2 ? "admin" : "user",
        status: i === count ? "inactive" : "active",
        login_count: Math.floor(Math.random() * 100),
        last_login_at: new Date(
          Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
        ).toISOString(),
        created_at: new Date(
          Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      });
    }
    return users;
  },

  // Generate demo tokens
  generateTokens: (count = 15) => {
    const tokens = [];
    const permissions = ["read_only", "read_write", "full"];
    const statuses = ["active", "inactive", "expired"];

    for (let i = 1; i <= count; i++) {
      tokens.push({
        id: i,
        user_id: Math.floor(Math.random() * 5) + 1,
        name: `Token ${i}`,
        token: `token_${Math.random().toString(36).substr(2, 20)}`,
        permission: permissions[Math.floor(Math.random() * permissions.length)],
        status: statuses[Math.floor(Math.random() * statuses.length)],
        description: `Demo token ${i}`,
        usage_count: Math.floor(Math.random() * 1000),
        last_used_at: new Date(
          Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
        ).toISOString(),
        created_at: new Date(
          Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      });
    }
    return tokens;
  },

  // Generate demo trades
  generateTrades: (count = 50) => {
    const trades = [];
    const exchanges = ["Binance", "Coinbase", "Kraken", "Huobi", "OKX"];
    const pairs = ["BTC/USDT", "ETH/USDT", "BTC/USD", "ETH/USD", "ADA/USDT"];

    for (let i = 1; i <= count; i++) {
      const exchange = exchanges[Math.floor(Math.random() * exchanges.length)];
      const pair = pairs[Math.floor(Math.random() * pairs.length)];

      trades.push({
        id: i,
        exchange,
        pair,
        volume: (Math.random() * 10000).toFixed(8),
        price: pair.includes("BTC")
          ? (40000 + Math.random() * 10000).toFixed(2)
          : (2000 + Math.random() * 1000).toFixed(2),
        timestamp: new Date(
          Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
        ).toISOString(),
        source: "API",
        token_id: Math.floor(Math.random() * 5) + 1,
        created_at: new Date(
          Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      });
    }
    return trades.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  },

  // Generate demo dashboard stats
  generateDashboardStats: () => ({
    total_users: 5,
    active_users: 4,
    total_tokens: 6,
    active_tokens: 4,
    total_trade_volume: 30,
    total_exchanges: 7,
    total_pairs: 12,
    today_volume: 5,
  }),
};

// Export the API service as default
export default apiService;

// Also export the axios instance for direct use if needed
export { api };
